import { Component, OnInit, inject } from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {DataService} from "../../../services/data.service";
import {AccessService} from "../../../services/access.service";
import {BackupService} from "../../../services/backup.service";
import {ViewMode} from "../../interfaces";
import Swal from "sweetalert2";
import {ToastrService} from "ngx-toastr";
import {MessageService} from "../../../services/message.service";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {PaginationComponent} from "ngx-bootstrap/pagination";
import {StatusPipe} from "../../../pipes/status.pipe";

interface Contact {
    id: string;
    id_customer: string;
    name: string;
    zip: number;
    city: string;
    street: string;
    phone: string;
    mail: string;
    _deleted: boolean;
    _editing: boolean;
}

@Component({
    selector: 'app-customer-detail',
    templateUrl: './customer-detail.component.html',
    imports: [
        NgIf,
        ReactiveFormsModule,
        FormsModule,
        PaginationComponent,
        StatusPipe,
        DatePipe,
        RouterLink,
        NgForOf
    ],
    styleUrl: './customer-detail.component.scss'
})
export class CustomerDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private backupService = inject(BackupService);
    private toastrService = inject(ToastrService);
    private messageService = inject(MessageService);

    readonly ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_customer!: string;
    viewMode!: ViewMode;
    customer: any;
    contacts: Contact[] = [];
    newContact: Contact;
    unassignedContacts: Contact[] = [];
    showAssignContacts = false;
    contactToAssign = null;

    // tasks
    paginConfig;
    entries;
    totalEntries;
    predicate;
    search;
    reverse;

    ngOnInit(): void {
        this.id_customer = this.route.snapshot.paramMap.get('id');
        this.viewMode = this.id_customer == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            number: [null, Validators.required],
            lastname: [null, Validators.required],
            lastname_2: [null],
            lastname_3: [null],
            firstname: [null],
            zip: [null, Validators.required],
            city: [null],
            street: [null],
            email: [null],
            email_2: [null],
            phone: [null],
            phone_mobile: [null],
            phone_private: [null],
            credit_rating: [null],
            created_at: [null],
            updated_at: [null],
        });

        if (this.id_customer !== '0') {
            this.getCustomer();

            this.search = {
                text: ''
            };
            this.paginConfig = {
                itemsPerPage: 25,
                page: 1
            };
            this.reverse = false;
            this.totalEntries = 0;
            this.predicate = 'name';
            this.listEntries(this.paginConfig);
        }

        this.dataService.request('Contact/listUnassigned', ).subscribe(response => {
            this.unassignedContacts = response.contacts;
        });
    }

    getCustomer(): void {
        this.dataService.request('Customer/get', {
            id_customer: this.id_customer
        }).subscribe(response => {
            this.customer = response.customer;
            this.contacts = response.contacts;

            this.contacts.forEach(contact => {
                contact._deleted = false;
                contact._editing = false;
            });

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.customer.id,
            number: this.customer.number,
            lastname: this.customer.lastname,
            lastname_2: this.customer.lastname_2,
            lastname_3: this.customer.lastname_3,
            firstname: this.customer.firstname,
            zip: this.customer.zip,
            city: this.customer.city,
            street: this.customer.street,
            email: this.customer.email,
            email_2: this.customer.email_2,
            phone: this.customer.phone,
            phone_mobile: this.customer.phone_mobile,
            phone_private: this.customer.phone_private,
            credit_rating: this.customer.credit_rating,
            created_at: this.customer.created_at,
            updated_at: this.customer.updated_at,
        });
    }

    save(): void {
        this.dataService.request('Customer/save', {
            customer: this.form.getRawValue(),
            contacts: this.contacts,
        }).subscribe(() => {
            this.router.navigate(['/customer']);
            this.toastrService.success('Der Kunde wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('Customer/update', {
            customer: this.form.getRawValue(),
            contacts: this.contacts,
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getCustomer();
            this.toastrService.success('Der Kunde wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/customer']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
        this.getCustomer();

        this.cancelAddContact();
        this.contacts.forEach(contact => {
            if (contact._editing) this.cancelEditContact(contact);
        });
        this.cancelShowContacts();
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Kunde wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Customer/delete', {
                    id_customer: this.id_customer
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Der Kunde wurde gelöscht');
                });
            }
        });
    }

    createTask(): void {
        this.messageService.setItem('create_task_customer', this.id_customer);
        this.router.navigate(['/task/0']);
    }

    // contacts

    addContact(): void {
        this.newContact = {
            id: null,
            id_customer: this.id_customer,
            name: '',
            zip: null,
            city: '',
            street: '',
            phone: '',
            mail: '',
            _deleted: false,
            _editing: false,
        }
    }

    cancelAddContact(): void {
        this.newContact = null;
    }

    confirmAddContact(): void {
        this.contacts.push(this.newContact);
        this.newContact = null;
    }

    deleteContact(contact): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Kontakt wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                contact._deleted = true;
            }
        });
    }

    editContact(contact): void {
        this.backupService.backup('contact', contact);
        contact._editing = true;
    }

    confirmEditContact(contact): void {
        contact._editing = false;
    }

    cancelEditContact(contact): void {
        contact = this.backupService.reset('contact', contact);
        contact._editing = false;
    }

    hasNoContacts(): boolean {
        return this.contacts.filter(contact => contact._deleted == false).length == 0;
    }

    editingContacts(): boolean {
        return this.contacts.some(contact => contact._editing == true);
    }

    // tasks

    setOrder(order): void {
        this.predicate = order;
        this.reverse = !this.reverse;
        this.paginConfig.page = 1;
        this.listEntries(this.paginConfig);
    }

    checkFilterAndList(): void {
        this.paginConfig.page = 1;
        this.listEntries(this.paginConfig);
    }

    listEntries(event): void {
        this.paginConfig = event;

        this.entries = null;
        this.dataService.request('Task/listByCustomer', {
            page: this.paginConfig.page,
            limit: this.paginConfig.itemsPerPage,
            order: this.predicate,
            reverse: this.reverse,
            filter: this.search.text,
            id_customer: this.id_customer,
        }).subscribe(response => {
            this.totalEntries = response.total;
            this.entries = response.tasks;
        });
    }

    showContacts(): void {
        this.showAssignContacts = true;
    }

    cancelShowContacts(): void {
        this.showAssignContacts = false;
    }

    assignContact(): void {
        this.contacts.push({
            id: this.contactToAssign.id,
            id_customer: this.id_customer,
            name: this.contactToAssign.name,
            zip: this.contactToAssign.zip,
            city: this.contactToAssign.city,
            street: this.contactToAssign.street,
            phone: this.contactToAssign.phone,
            mail: this.contactToAssign.mail,
            _deleted: false,
            _editing: false,
        });
        this.showAssignContacts = false;
        this.unassignedContacts.splice(this.unassignedContacts.indexOf(this.contactToAssign), 1);
    }
}
